import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import _ from 'lodash';

import { PendingChangesActionType } from 'constants/pendingChanges';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';

import {
  loadCustomFieldValuesAction,
  saveManualCustomFieldValuesAction,
} from 'store/loanApplications/actions/customFieldValues.actions';
import { selectCustomFieldValuesByApplicationId } from 'store/loanApplications/selectors/customFieldValues.selectors';
import { selectCustomFields } from 'store/system/selectors/customFieldDefinitions.selectors';

import {
  buildBackendPendingChangesMap,
  buildInitialFormValues,
  buildSavePayload,
  computeDirtyValues,
  computeFieldErrors,
  filterDisplayableEnabledDefinitionsForTarget,
} from '../../CustomFieldsSection.utils';

import { buildFormStateKey } from './utils';

const useCustomFieldsValidation = ({
  applicationId,
  target,
  targetEntityId,
  definitionIds = null,
  skip = false,
}) => {
  const dispatch = useDispatch();

  const allDefinitions = useSelector(selectCustomFields);
  const selectValues = useMemo(
    () => selectCustomFieldValuesByApplicationId(applicationId),
    [applicationId],
  );
  const values = useSelector(selectValues);

  const definitionIdsKey = useMemo(
    () => (definitionIds ? [...definitionIds].sort().join(',') : null),
    [definitionIds],
  );

  const definitions = useMemo(() => {
    const displayableDefinitions = filterDisplayableEnabledDefinitionsForTarget(
      allDefinitions,
      target,
    );

    if (_.isNil(definitionIdsKey)) {
      return displayableDefinitions;
    }

    const allowedIds = new Set(definitionIdsKey ? definitionIdsKey.split(',') : []);

    return displayableDefinitions.filter(definition => allowedIds.has(definition.id));
  }, [allDefinitions, target, definitionIdsKey]);

  const initialValues = useMemo(
    () => buildInitialFormValues({ definitions, values, targetEntityId }),
    [definitions, values, targetEntityId],
  );

  const backendPendingChanges = useMemo(
    () => buildBackendPendingChangesMap({ definitions, values, targetEntityId }),
    [definitions, values, targetEntityId],
  );

  const stateKey = useMemo(
    () => buildFormStateKey({ target, targetEntityId }),
    [target, targetEntityId],
  );

  const [formValuesByKey, setFormValuesByKey] = useState({});
  const [touchedFieldsByKey, setTouchedFieldsByKey] = useState({});
  const lastInitialValuesByKeyRef = useRef({});

  useEffect(() => {
    const lastInitialValues = lastInitialValuesByKeyRef.current[stateKey];

    if (_.isEqual(lastInitialValues, initialValues)) {
      return;
    }

    lastInitialValuesByKeyRef.current = {
      ...lastInitialValuesByKeyRef.current,
      [stateKey]: initialValues,
    };

    setFormValuesByKey(prev => {
      const previousFormValues = prev[stateKey];
      const isUntouchedForKey =
        _.isNil(previousFormValues) || _.isEqual(previousFormValues, lastInitialValues);

      if (!isUntouchedForKey) {
        return prev;
      }

      return { ...prev, [stateKey]: initialValues };
    });
  }, [initialValues, stateKey]);

  useEffectSafeStrictMode(() => {
    if (skip || !applicationId) {
      return;
    }

    dispatch(loadCustomFieldValuesAction({ applicationId }));
  }, [applicationId, dispatch, skip]);

  const formValues = formValuesByKey[stateKey] ?? initialValues;
  const touchedFields = useMemo(
    () => touchedFieldsByKey[stateKey] ?? {},
    [touchedFieldsByKey, stateKey],
  );

  const setFieldValue = useCallback(
    (definitionId, nextValue) => {
      setFormValuesByKey(prev => {
        const current = prev[stateKey] ?? lastInitialValuesByKeyRef.current[stateKey] ?? {};

        return { ...prev, [stateKey]: { ...current, [definitionId]: nextValue } };
      });
      setTouchedFieldsByKey(prev => {
        const current = prev[stateKey] ?? {};

        if (current[definitionId]) {
          return prev;
        }

        return { ...prev, [stateKey]: { ...current, [definitionId]: true } };
      });
    },
    [stateKey],
  );

  const setFieldTouched = useCallback(
    definitionId => {
      setTouchedFieldsByKey(prev => {
        const current = prev[stateKey] ?? {};

        if (current[definitionId]) {
          return prev;
        }

        return { ...prev, [stateKey]: { ...current, [definitionId]: true } };
      });
    },
    [stateKey],
  );

  const allErrors = useMemo(
    () => computeFieldErrors({ definitions, formValues }),
    [definitions, formValues],
  );

  const errors = useMemo(
    () =>
      Object.keys(allErrors).reduce((acc, definitionId) => {
        if (touchedFields[definitionId]) {
          acc[definitionId] = allErrors[definitionId];
        }

        return acc;
      }, {}),
    [allErrors, touchedFields],
  );

  const isValid = !Object.keys(allErrors).length;

  const dirtyValues = useMemo(
    () => computeDirtyValues({ formValues, initialValues, definitions }),
    [formValues, initialValues, definitions],
  );

  const changedFields = useMemo(
    () =>
      Object.keys(dirtyValues).reduce((acc, definitionId) => {
        acc[definitionId] = true;

        return acc;
      }, {}),
    [dirtyValues],
  );

  const dirtyErrors = useMemo(
    () =>
      Object.keys(allErrors).reduce((acc, definitionId) => {
        if (definitionId in dirtyValues) {
          acc[definitionId] = allErrors[definitionId];
        }

        return acc;
      }, {}),
    [allErrors, dirtyValues],
  );

  const isDirtyValid = !Object.keys(dirtyErrors).length;

  const pendingChangesByDefinition = useMemo(
    () =>
      definitions.reduce((acc, definition) => {
        const beActionType = backendPendingChanges[definition.id];
        const localActionType = changedFields[definition.id]
          ? PendingChangesActionType.MODIFIED
          : null;
        const actionType = beActionType ?? localActionType;

        if (actionType) {
          acc[definition.id] = actionType;
        }

        return acc;
      }, {}),
    [definitions, backendPendingChanges, changedFields],
  );

  const pendingChangesCount = Object.keys(pendingChangesByDefinition).length;

  const markAllTouched = useCallback(() => {
    setTouchedFieldsByKey(prev => ({
      ...prev,
      [stateKey]: definitions.reduce((acc, definition) => {
        acc[definition.id] = true;

        return acc;
      }, {}),
    }));
  }, [definitions, stateKey]);

  const save = useCallback(async () => {
    if (!isDirtyValid) {
      markAllTouched();

      throw new Error('Custom fields validation failed');
    }

    if (!Object.keys(dirtyValues).length) {
      return null;
    }

    const payload = buildSavePayload({ dirtyValues, targetEntityId, definitions });

    if (!payload.length) {
      return null;
    }

    return dispatch(saveManualCustomFieldValuesAction({ applicationId, values: payload }));
  }, [
    applicationId,
    definitions,
    dirtyValues,
    dispatch,
    isDirtyValid,
    markAllTouched,
    targetEntityId,
  ]);

  const discard = useCallback(() => {
    setFormValuesByKey(prev => _.omit(prev, stateKey));
    setTouchedFieldsByKey(prev => _.omit(prev, stateKey));
  }, [stateKey]);

  return {
    definitions,
    formValues,
    setFieldValue,
    setFieldTouched,
    errors,
    isValid,
    isDirtyValid,
    dirtyErrors,
    dirtyValues,
    changedFields,
    markAllTouched,
    pendingChangesByDefinition,
    pendingChangesCount,
    save,
    discard,
  };
};

export default useCustomFieldsValidation;
